/**
 * Invio ritardato di 5 secondi con "Annulla".
 *
 * Il messaggio in attesa e' legato alla chat in cui e' stato scritto. Se si cambia chat
 * (o si chiude il pannello) prima dei 5 secondi, NON parte: l'invio userebbe la chat
 * aperta in quel momento, cioe' quella sbagliata. Torna invece nella bozza della sua
 * chat e lo si dice (onAbandon). Prima il timer veniva solo cancellato: il messaggio
 * spariva, la barra restava ferma e "Annulla" portava il testo nella chat nuova.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export const UNDO_SECONDS = 5;

export interface PendingSend { text: string; mentions: string[]; chatId: string }

export function useUndoSend(
  chatId: string,
  doSend: (text: string, mentions: string[]) => void,
  onAbandon: (pending: PendingSend) => void,
) {
  const [pending, setPending] = useState<PendingSend | null>(null);
  const [countdown, setCountdown] = useState(0);
  const pendingRef = useRef<PendingSend | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const doSendRef = useRef(doSend);
  const onAbandonRef = useRef(onAbandon);
  doSendRef.current = doSend;
  onAbandonRef.current = onAbandon;

  const stopTimers = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    timerRef.current = null;
    intervalRef.current = null;
  };

  const reset = useCallback(() => {
    stopTimers();
    pendingRef.current = null;
    setPending(null);
    setCountdown(0);
  }, []);

  const start = useCallback((text: string, mentions: string[]) => {
    stopTimers();
    const p: PendingSend = { text, mentions, chatId };
    pendingRef.current = p;
    setPending(p);
    setCountdown(UNDO_SECONDS);
    intervalRef.current = setInterval(() => setCountdown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    timerRef.current = setTimeout(() => {
      const current = pendingRef.current;
      reset();
      if (current) doSendRef.current(current.text, current.mentions);
    }, UNDO_SECONDS * 1000);
  }, [chatId, reset]);

  /** Annulla e restituisce il messaggio (per rimetterlo nel campo), se c'era. */
  const cancel = useCallback((): PendingSend | null => {
    const current = pendingRef.current;
    reset();
    return current;
  }, [reset]);

  // Cambio chat o chiusura del pannello: il messaggio in attesa torna nella sua bozza.
  useEffect(() => {
    return () => {
      const current = pendingRef.current;
      if (current) {
        stopTimers();
        pendingRef.current = null;
        onAbandonRef.current(current);
      }
      setPending(null);
      setCountdown(0);
    };
  }, [chatId]);

  return { pending, countdown, start, cancel };
}
